import { useMemo } from "react";
import type { ExternalContactRow, OrgUserOption, UserDirectoryRow } from "@/lib/contactsDirectoryClient";
import type { DirectoryListingFilter } from "./adminContactsDirectoryShared";

export type DirectoryDisplayRow = {
  kind: "external" | "user";
  id: string;
  name: string;
  email: string;
  phone: string;
  company: string;
  title: string;
  department: string;
  contact_path: string;
  visibility_level: number | null;
  max_downstream_visibility_level: number | null;
  affiliation: string | null;
  hierarchy_participation: string | null;
  is_active: boolean;
  sourceRow: ExternalContactRow | UserDirectoryRow;
};

type Params = {
  external: ExternalContactRow[];
  userDir: UserDirectoryRow[];
  orgUsers: OrgUserOption[];
  directoryListingFilter: DirectoryListingFilter;
  showDeactivatedExternal: boolean;
  externalSearch: string;
  externalPage: number;
  externalVisibleCount: 5 | 10 | 20;
  extFormTopics: string;
  extFormContactPath: string;
  parseTopicsText: (value: unknown) => string[];
  normalizeEmail: (value: string | null | undefined) => string;
  contactPathOptions: readonly string[];
  topicOptions: readonly string[];
};

export function useAdminContactsDirectoryDerived({
  external,
  userDir,
  orgUsers,
  directoryListingFilter,
  showDeactivatedExternal,
  externalSearch,
  externalPage,
  externalVisibleCount,
  extFormTopics,
  extFormContactPath,
  parseTopicsText,
  normalizeEmail,
  contactPathOptions,
  topicOptions,
}: Params) {
  const availablePaths = useMemo(() => {
    const set = new Set<string>(contactPathOptions);
    if (extFormContactPath.trim()) set.add(extFormContactPath.trim());
    for (const row of external) if (row.contact_path?.trim()) set.add(row.contact_path.trim());
    for (const row of userDir) if (row.contact_path?.trim()) set.add(row.contact_path.trim());
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [contactPathOptions, extFormContactPath, external, userDir]);

  const availableTopics = useMemo(() => {
    const set = new Set<string>(topicOptions);
    for (const t of parseTopicsText(extFormTopics)) set.add(t);
    for (const row of external) {
      const arr = Array.isArray(row.topics) ? (row.topics as unknown[]) : [];
      for (const t of arr) {
        const tt = String(t || "").trim();
        if (tt) set.add(tt);
      }
    }
    for (const row of userDir) {
      const arr = Array.isArray(row.topics) ? (row.topics as unknown[]) : [];
      for (const t of arr) {
        const tt = String(t || "").trim();
        if (tt) set.add(tt);
      }
    }
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [extFormTopics, external, parseTopicsText, topicOptions, userDir]);

  const topicDirectoryUsage = useMemo(() => {
    const counts = new Map<string, number>();
    const bump = (topicsRaw: unknown) => {
      const uniq = new Set(parseTopicsText(topicsRaw));
      for (const t of uniq) counts.set(t, (counts.get(t) ?? 0) + 1);
    };
    for (const row of external) {
      if (!showDeactivatedExternal && !row.is_active) continue;
      bump(row.topics);
    }
    for (const row of userDir) {
      if (!showDeactivatedExternal && !row.is_active) continue;
      bump(row.topics);
    }
    const keys = new Set<string>([...topicOptions]);
    for (const t of parseTopicsText(extFormTopics)) keys.add(t);
    for (const t of counts.keys()) keys.add(t);
    return [...keys]
      .map((topic) => ({ topic, count: counts.get(topic) ?? 0 }))
      .sort((a, b) => b.count - a.count || a.topic.localeCompare(b.topic));
  }, [extFormTopics, external, parseTopicsText, showDeactivatedExternal, topicOptions, userDir]);

  const filteredDirectoryRows = useMemo(() => {
    const baseExternal: DirectoryDisplayRow[] = external
      .filter((r) => (showDeactivatedExternal ? true : r.is_active))
      .map((r) => ({
        kind: "external",
        id: r.id,
        name: r.name,
        email: r.email ?? "",
        phone: r.phone ?? "",
        company: r.company ?? "",
        title: r.title ?? "",
        department: r.department ?? "",
        contact_path: r.contact_path ?? "",
        visibility_level: r.visibility_level,
        max_downstream_visibility_level: r.max_downstream_visibility_level ?? null,
        affiliation: r.affiliation ?? null,
        hierarchy_participation: r.hierarchy_participation ?? null,
        is_active: r.is_active,
        sourceRow: r,
      }));
    const baseUsers: DirectoryDisplayRow[] = userDir
      .filter((r) => (showDeactivatedExternal ? true : r.is_active))
      .map((r) => {
        const p = r.profiles;
        const profPhone = String(p?.personal_phone || "").trim() || String(p?.work_phone || "").trim() || "";
        return {
          kind: "user",
          id: r.id,
          name: String(p?.full_name || p?.display_name || p?.email || r.profile_id || "").trim() || r.profile_id,
          email: p?.email ?? "",
          phone: profPhone,
          company: "",
          title: "",
          department: r.department ?? "",
          contact_path: r.contact_path ?? "",
          visibility_level: r.visibility_level,
          max_downstream_visibility_level: r.max_downstream_visibility_level ?? null,
          affiliation: r.affiliation ?? null,
          hierarchy_participation: r.hierarchy_participation ?? null,
          is_active: r.is_active,
          sourceRow: r,
        };
      });
    const base = [...baseUsers, ...baseExternal];
    const byListing =
      directoryListingFilter === "users"
        ? base.filter((r) => r.kind === "user")
        : directoryListingFilter === "non_users"
          ? base.filter((r) => r.kind === "external")
          : directoryListingFilter === "org_staff"
            ? base.filter((r) => r.kind === "user" || String(r.affiliation || "org_affiliated").toLowerCase() !== "vendor_partner")
            : directoryListingFilter === "vendors"
              ? base.filter((r) => r.kind === "external" && String(r.affiliation || "org_affiliated").toLowerCase() === "vendor_partner")
              : base;

    const needle = externalSearch.trim().toLowerCase();
    const searched = !needle
      ? byListing
      : byListing.filter((r) => {
          const topicsRaw = r.sourceRow.topics;
          const topics = Array.isArray(topicsRaw) ? topicsRaw.map((t) => String(t || "")).join(" ") : "";
          const hay = [r.name, r.email, r.phone, r.company, r.title, r.department, r.contact_path, topics]
            .map((x) => String(x || "").toLowerCase())
            .join(" | ");
          return hay.includes(needle);
        });
    return [...searched].sort((a, b) => a.name.localeCompare(b.name));
  }, [directoryListingFilter, external, externalSearch, showDeactivatedExternal, userDir]);

  const visibleDirectoryRows = useMemo(() => {
    const start = (externalPage - 1) * externalVisibleCount;
    return filteredDirectoryRows.slice(start, start + externalVisibleCount);
  }, [externalPage, externalVisibleCount, filteredDirectoryRows]);
  const externalTotalPages = Math.max(1, Math.ceil(filteredDirectoryRows.length / externalVisibleCount));

  const externalManagerOptions = useMemo(
    () =>
      external
        .filter((row) => row.is_active)
        .map((row) => ({
          id: row.id,
          label: `${row.name}${row.title ? ` - ${row.title}` : ""}${row.email ? ` (${row.email})` : ""}`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [external]
  );

  const userManagerOptions = useMemo(() => {
    const byProfile = new Map<string, { profile_id: string; label: string }>();
    for (const row of userDir.filter((r) => r.is_active)) {
      if (byProfile.has(row.profile_id)) continue;
      const p = row.profiles;
      const name = String(p?.full_name || p?.display_name || "").trim();
      const email = String(p?.email || "").trim();
      const label = `${name || row.profile_id}${email ? ` (${email})` : ""}`;
      byProfile.set(row.profile_id, { profile_id: row.profile_id, label });
    }
    return [...byProfile.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [userDir]);

  const userManagerProfileIdSet = useMemo(() => new Set(userManagerOptions.map((u) => u.profile_id)), [userManagerOptions]);
  const orgProfileIdByEmail = useMemo(() => {
    const byEmail = new Map<string, string>();
    for (const row of userDir) {
      const email = normalizeEmail(row.profiles?.email);
      if (email && row.profile_id && !byEmail.has(email)) byEmail.set(email, row.profile_id);
    }
    for (const user of orgUsers) {
      const email = normalizeEmail(user.profiles?.email);
      if (email && user.profile_id && !byEmail.has(email)) byEmail.set(email, user.profile_id);
    }
    return byEmail;
  }, [normalizeEmail, orgUsers, userDir]);
  const orgReportingManagerOptions = useMemo(
    () => [
      ...userManagerOptions.map((u) => ({ key: `user:${u.profile_id}`, value: u.profile_id, label: `${u.label} (app user)` })),
      ...externalManagerOptions.map((e) => ({ key: `external:${e.id}`, value: e.id, label: `${e.label} (external)` })),
    ],
    [externalManagerOptions, userManagerOptions]
  );

  const fallbackCandidates = useMemo(() => {
    const byProfileId = new Map<string, { key: string; label: string }>();
    const fromUsers = userDir
      .filter((row) => row.is_active && !row.location_id && String(row.scope || "").toLowerCase() === "org")
      .map((row) => {
        const p = row.profiles;
        const label = String(p?.full_name || p?.display_name || p?.email || row.profile_id || "").trim() || row.profile_id;
        const option = {
          key: `user:${row.id}`,
          label: `${label} (app user)`,
        };
        byProfileId.set(row.profile_id, option);
        return option;
      });
    const fromOrgUsers = orgUsers
      .filter((u, idx, arr) => !byProfileId.has(u.profile_id) && arr.findIndex((x) => x.profile_id === u.profile_id) === idx)
      .map((u) => {
        const p = u.profiles;
        const label = String(p?.full_name || p?.display_name || p?.email || u.profile_id || "").trim() || u.profile_id;
        return {
          key: `user:${u.profile_id}`,
          label: `${label} (app user)`,
        };
      });
    const fromExternal = external
      .filter((row) => row.is_active && !row.location_id && String(row.scope || "").toLowerCase() === "org")
      .map((row) => ({
        key: `external:${row.id}`,
        label: `${row.name}${row.title ? ` - ${row.title}` : ""} (external)`,
      }));
    return [...fromUsers, ...fromOrgUsers, ...fromExternal].sort((a, b) => a.label.localeCompare(b.label));
  }, [external, orgUsers, userDir]);

  return {
    availablePaths,
    availableTopics,
    topicDirectoryUsage,
    filteredDirectoryRows,
    visibleDirectoryRows,
    externalTotalPages,
    externalManagerOptions,
    userManagerOptions,
    userManagerProfileIdSet,
    orgProfileIdByEmail,
    orgReportingManagerOptions,
    fallbackCandidates,
  };
}
